import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CircularSpinner from 'shared/atoms/CircularSpinner';
import TooltipGraphBasic from 'shared/atoms/LineGraph/components/TooltipGraphBasic';
import T from 'shared/atoms/Typography';

import { TIMESTAMP_FORMATS } from 'shared/const';
import theme from 'shared/themes/default';
import { firstLetterCaps, formatDate } from 'shared/utils';

import WatchDataEpisodesSeen from './components/WatchDataEpisodesSeen';
import {
  OuterWrapper,
  GraphOuterWrapper,
  TotalEpisodesLabelWrapper,
  SpinnerWrapper,
} from './styled';

const LineGraph = React.lazy(() => import('shared/atoms/LineGraph'));

const computeTotals = (data, field) => {
  let total = 0;
  data.forEach((element) => {
    if (element[field]) {
      total += element[field];
    }
  });
  return total;
};

const mapAggregatedWatchDataGraph = (data, locale) => {
  const processedData = [];

  for (let index = 0; index < data.length / 2; index += 1) {
    const point1 = data[index + data.length / 2];
    const point2 = data[index];
    const month = point1.date.split('-')[1];
    const date = new Date();
    date.setMonth(month - 1, 1);

    processedData.push({
      name: firstLetterCaps(
        formatDate({
          date,
          locale,
          type: TIMESTAMP_FORMATS.MONTH,
        })
      ).replace('.', ''),
      current: point1.count,
      past: point2.count,
    });
  }

  return processedData;
};

const EpisodesSeenGraph = ({ data, loading, spinnerDuration }) => {
  const { t, i18n } = useTranslation();

  const processedData = mapAggregatedWatchDataGraph(data, i18n.language);

  const period = processedData.length;
  const currentSeen = computeTotals(processedData, 'current');
  const pastSeen = computeTotals(processedData, 'past');

  const config = {
    colors: [theme.palette.brand, theme.palette.disabledLight],
    dataKeys: ['current', 'past'],
    nameKey: 'name',
    labels: [
      { key: 'watchData.lastXMonths', info: { months: period } },
      { key: 'watchData.samePeriodYearBefore' },
    ],
  };

  const customTooltip = {
    Component: TooltipGraphBasic,
    labels: [
      { key: 'watchData.tooltipCurrent' },
      { key: 'watchData.tooltipPast' },
    ],
  };

  return (
    <>
      <OuterWrapper>
        <WatchDataEpisodesSeen
          episodesSeen={currentSeen}
          episodesSeenPreviousYear={pastSeen}
          timePeriodText={t('watchData.lastXMonths', { months: period })}
        />
      </OuterWrapper>
      <GraphOuterWrapper>
        {loading ? (
          <SpinnerWrapper>
            <CircularSpinner
              static
              showProgress
              duration={spinnerDuration}
              size={98}
            />
          </SpinnerWrapper>
        ) : (
          <>
            <TotalEpisodesLabelWrapper>
              <T fontWeight="semibold" color={theme.palette.lightInteracting}>
                {t('watchData.titleEpisodesSeen')}
              </T>
            </TotalEpisodesLabelWrapper>
            <React.Suspense fallback={<CircularSpinner static />}>
              <LineGraph
                data={processedData}
                config={config}
                customTooltip={customTooltip}
              />
            </React.Suspense>
          </>
        )}
      </GraphOuterWrapper>
    </>
  );
};

EpisodesSeenGraph.propTypes = {
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  spinnerDuration: PropTypes.number,
};

EpisodesSeenGraph.defaultProps = {
  spinnerDuration: 10,
};

export default EpisodesSeenGraph;
